/**
 * useNavPromotions — fetches the editable featured visuals shown in the
 * navbar mega-menus.
 *
 * The Navbar renders on every page, so we share a single in-memory cache
 * keyed by URL and only re-fetch on window focus (to pick up admin edits).
 * The backend already caches the public endpoint (60 s max-age), so a
 * cold tab opening just hits the edge CDN.
 *
 * Returns `{ slots, loading }` where `slots` is `{ buy, rent, land }` or
 * an empty object while loading. Consumers must merge with their built-in
 * defaults so a failed fetch never breaks rendering.
 */
import { useEffect, useState } from 'react';
import { navPromotionsAPI } from '../utils/api';

let _cache = null;
let _inflight = null;

const fetchPromotions = () => {
  if (_inflight) return _inflight;
  _inflight = navPromotionsAPI
    .getPublic()
    .then((res) => {
      _cache = res.data?.slots || {};
      return _cache;
    })
    .catch(() => {
      _cache = _cache || {};
      return _cache;
    })
    .finally(() => {
      _inflight = null;
    });
  return _inflight;
};

const invalidate = () => {
  _cache = null;
  return fetchPromotions();
};

export const useNavPromotions = () => {
  const [slots, setSlots] = useState(_cache || {});
  const [loading, setLoading] = useState(_cache === null);

  useEffect(() => {
    let alive = true;
    if (_cache) {
      setSlots(_cache);
      setLoading(false);
    } else {
      fetchPromotions().then((res) => {
        if (alive) {
          setSlots(res);
          setLoading(false);
        }
      });
    }
    // Re-fetch on focus so admin edits propagate without a hard reload.
    const onFocus = () => {
      invalidate().then((res) => {
        if (alive) setSlots(res);
      });
    };
    window.addEventListener('focus', onFocus);
    // Also react to an explicit admin event (in-tab edits).
    window.addEventListener('realty237:nav-promotions-updated', onFocus);
    return () => {
      alive = false;
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('realty237:nav-promotions-updated', onFocus);
    };
  }, []);

  return { slots, loading };
};

// Allow the admin tab to broadcast in-tab updates after a successful save.
export const broadcastNavPromotionsUpdate = () => {
  window.dispatchEvent(new CustomEvent('realty237:nav-promotions-updated'));
};
